import React, { useState, useEffect } from 'react';
import { X, Copy, Check, ExternalLink } from 'lucide-react';
import './RewardSpinner.css';

/**
 * SpinResultModal - Shows the winning result with confetti
 * 
 * Now includes plan category display and navigates to correct pricing tab
 */
const SpinResultModal = ({ result, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  // Stop confetti after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.coupon_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClaimNow = () => {
    // Navigate to pricing page with correct tab, coupon code, and target plan ID
    const tab = result.plan_category || 'stock_recommendations';
    const coupon = encodeURIComponent(result.coupon_code || '');
    // Use plan_id from backend response (spinner coupons are now plan-specific)
    const planId = result.plan_id || '';
    if (planId) {
      // Send the user straight to the exact plan page with the coupon pre-filled
      window.location.href = `/plans/${planId}?coupon=${coupon}`;
    } else {
      // Fallback when the coupon isn't tied to a specific plan: open the pricing
      // page on the right tab with the coupon ready to apply (never a blank /plans/ page)
      window.location.href = `/pricing?tab=${tab}&coupon=${coupon}`;
    }
  };

  // Format the display name for plan category
  const getPlanCategoryDisplay = () => {
    if (result.plan_category_display) {
      return result.plan_category_display;
    }
    // Fallback formatting
    if (result.plan_category === 'stock_recommendations') {
      return 'Stock Recommendations';
    }
    if (result.plan_category === 'live_corporate_announcements') {
      return 'Live Corporate Announcements';
    }
    if (result.plan_category === 'all') {
      return 'All Plans';
    }
    return result.plan_category || '';
  };

  // Build the prize display text
  const getPrizeDisplayText = () => {
    const category = getPlanCategoryDisplay();
    const discountType = result.discount_type || 'percentage';
    
    let discountText = '';
    if (discountType === 'first_month_free') {
      discountText = 'First Month Free';
    } else if (result.discount_percentage) {
      discountText = `${result.discount_percentage}% Off`;
    }
    
    // Use plan_name from backend if available, otherwise extract from offer_name
    let planName = result.plan_name || '';
    if (!planName && result.offer_name) {
      // Extract plan name from offer_name (e.g., "Advance 90% Off" -> "Advance")
      planName = (result.offer_name || '').split(/\s+\d+%/)[0].trim();
    }
    
    // Always add "Plan" after the plan name
    const formattedOffer = planName ? `${planName} Plan ${discountText}` : discountText;
    
    if (category && category !== 'All Plans') {
      return `${category} - ${formattedOffer}`;
    }
    return formattedOffer;
  };

  // Generate confetti pieces with varied properties
  const confettiColors = ['#FF6B35', '#9B59B6', '#3498DB', '#2ECC71', '#F1C40F', '#E74C3C', '#FFD700', '#FF69B4'];

  return (
    <>
      {/* CSS Confetti celebration */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(80)].map((_, i) => (
            <div 
              key={i} 
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`,
                backgroundColor: confettiColors[i % confettiColors.length],
                width: `${8 + Math.random() * 8}px`,
                height: `${8 + Math.random() * 8}px`,
              }}
            />
          ))}
        </div>
      )}

      <div className="result-modal-overlay">
        <div className="result-modal">
          <button className="result-close-btn" onClick={onClose}>
            <X size={24} />
          </button>

          <div className="result-content">
            {/* Celebration emoji */}
            <div className="celebration-emoji">🎉</div>
            
            {/* Title */}
            <h2 className="result-title">Congratulations!</h2>
            
            {/* Prize */}
            <div className="prize-box">
              <span className="prize-label">You Won:</span>
              <span className="prize-name">{getPrizeDisplayText()}</span>
            </div>
            
            {/* Coupon code */}
            <div className="coupon-box">
              <span className="coupon-label">Your Coupon Code:</span>
              <div className="coupon-code-container">
                <code className="coupon-code">{result.coupon_code}</code>
                <button 
                  className="copy-btn"
                  onClick={handleCopy}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
              {copied && <span className="copied-text">Copied!</span>}
            </div>
            
            {/* Validity info */}
            {result.valid_until && (
              <p className="validity-info">
                Valid until: {new Date(result.valid_until).toLocaleDateString()}
              </p>
            )}
            
            {/* Discount info */}
            {result.discount_percentage && (
              <div className="discount-badge">
                {result.discount_percentage}% OFF
              </div>
            )}
            
            {/* CTA Button */}
            <button className="claim-now-btn" onClick={handleClaimNow}>
              <span>Use This Coupon Now!</span>
              <ExternalLink size={18} />
            </button>
            
            {/* Instructions */}
            <p className="instructions">
              Apply this code at checkout to redeem your discount.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpinResultModal;
